import dayjs from 'dayjs';
import { Box, HStack, IconButtonWithTooltip, SimpleGrid, Text, VStack } from 'design-system';
import { CopyIcon } from '../icons/copy-icon';
import { RestaurantInfoCell } from './restaurant-info-cell';
import { SupplierInfoCell } from './supplier-info-cell';

interface IInfoRow {
  title: string;
  body: string;
  enableBodyCopyButton?: boolean;
  titleColor?: string;
}

const InfoRow = ({ title, body, titleColor = 'gray.500', enableBodyCopyButton = false }: IInfoRow) => {
  const onCopy = () => {
    return navigator.clipboard.writeText(body);
  };

  return (
    <HStack spacing='sm'>
      <VStack spacing='2xs' align='start' flex={1}>
        <Text textTransform='capitalize' color={titleColor} fontSize='sm'>
          {title}
        </Text>
        <Text color='gray.800' fontSize='md' fontWeight='500'>
          {body ?? '---'}
        </Text>
      </VStack>

      {!!enableBodyCopyButton && (
        <IconButtonWithTooltip
          label='Copy'
          aria-label='copy'
          Icon={<CopyIcon />}
          onClick={onCopy}
          variant='ghost'
          disabled={!body}
        />
      )}
    </HStack>
  );
};

interface IRestaurantDetails {
  restaurantId: string;
  name: string;
  subEntity: string;
  phoneNumber: string;
}

const RestaurantDetails = ({ restaurantId, name, subEntity, phoneNumber }: IRestaurantDetails) => {
  return (
    <Box>
      <Text mb='xs' color='gray.600' fontWeight='bold'>
        Restaurant Details
      </Text>

      <RestaurantInfoCell
        restaurantId={restaurantId}
        name={name}
        subEntityName={subEntity}
        phoneNumber={phoneNumber}
        spacing='0'
      />
    </Box>
  );
};

interface ISupplierDetails {
  supplierId: string;
  name: string;
  supplierUserName: string;
  phoneNumber: string;
}

const SupplierDetails = ({ supplierId, name, supplierUserName, phoneNumber }: ISupplierDetails) => {
  return (
    <Box>
      <Text mb='xs' color='gray.600' fontWeight='bold'>
        Supplier Details
      </Text>

      <SupplierInfoCell
        supplierId={supplierId}
        name={name}
        supplierUserName={supplierUserName}
        phoneNumber={phoneNumber}
        spacing='0'
      />
    </Box>
  );
};

interface IOrderDetailsSection {
  order: any;
}

export const OrderDetailsSection = ({ order }: IOrderDetailsSection) => {
  return (
    // Two columns while the card is wide enough for two 280 px ones, one below:
    // no fixed minimum widths, so the page never scrolls sideways.
    <SimpleGrid minChildWidth='280px' spacing='lg' pt='md'>
      <VStack spacing='md' align='stretch' minW={0}>
        <InfoRow title='Invoice Number' body={order?.invoiceNumber} enableBodyCopyButton />
        <InfoRow title='Delivery Details' body={order?.restaurant?.subEntity?.address} enableBodyCopyButton />
        <InfoRow title='Expected Delivery Date' body={dayjs(order?.deliveryDate).format('DD-MM-YYYY')} />
        <InfoRow title='Delivery Instructions' body={order?.note} />
        {order?.comment && <InfoRow title='Note' body={order?.comment} />}
      </VStack>

      <VStack spacing='lg' align='stretch' minW={0}>
        <RestaurantDetails
          restaurantId={order?.restaurant?.id}
          name={order?.restaurant?.name}
          subEntity={order?.restaurant?.subEntity?.name}
          phoneNumber={order?.restaurant?.user?.phoneNumber}
        />

        <SupplierDetails
          supplierId={order?.supplier?.id}
          name={order?.supplier?.name}
          supplierUserName={order?.supplier?.user?.name}
          phoneNumber={order?.supplier?.user?.phoneNumber}
        />

        <HStack spacing='lg'>
          <InfoRow
            title='Supplier Cutoff Time'
            body={order?.supplier?.cutOffTime ? dayjs(order?.supplier?.cutOffTime).format('hh:mm A') : '---'}
          />
          <InfoRow title='Is Public' body={order?.supplier?.isPublic ? 'Yes' : 'No'} />
        </HStack>
      </VStack>
    </SimpleGrid>
  );
};
